import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiMenu, FiCalendar, FiDollarSign, FiUser,
    FiLogOut, FiChevronDown, FiAlertTriangle, FiCheck, FiHome,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";

// ─── College Selector Dropdown (for Super Admin) ──────────────────────────────
export function CollegeSelector({ label = "Campus", value, colleges = [], onSelect }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button
                type="button"
                onClick={() => setOpen((p) => !p)}
                style={{
                    display: "flex", alignItems: "center", gap: "0.4rem",
                    background: "#f0fdf4", border: "1px solid #bbf7d0",
                    borderRadius: 8, padding: "5px 10px", cursor: "pointer",
                    fontSize: 12.5, color: "#166534", transition: "all 0.15s",
                    whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#dcfce7"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#f0fdf4"; }}
            >
                <FiHome size={13} color="#16a34a" />
                <span style={{ fontSize: 10, color: "#15803d", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {label}:
                </span>
                <span style={{ fontWeight: 700, maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {value || "All Campuses"}
                </span>
                <FiChevronDown size={11} color="#16a34a" style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }} />
            </button>

            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 6px)", left: 0,
                    minWidth: 260, background: "#fff", border: "1px solid var(--border)",
                    borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    zIndex: 100, overflow: "hidden",
                }}>
                    <div style={{ padding: "0.4rem 0.75rem", fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid var(--border)" }}>
                        Select Active Campus
                    </div>
                    <button type="button" onClick={() => { onSelect("all"); setOpen(false); }}
                        style={{
                            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "0.55rem 0.85rem", background: value === "All Campuses" ? "#f0fdf4" : "transparent",
                            border: "none", cursor: "pointer", fontSize: 13, fontWeight: value === "All Campuses" ? 700 : 400,
                            color: value === "All Campuses" ? "#166534" : "var(--text)", textAlign: "left",
                            borderBottom: "1px solid #f1f5f9"
                        }}
                    >
                        <span>🌐 All Colleges (Group Overview)</span>
                        {value === "All Campuses" && <FiCheck size={13} color="#16a34a" />}
                    </button>
                    {colleges.map((c) => {
                        const isSelected = c.name === value;
                        return (
                            <button key={c._id} type="button" onClick={() => { onSelect(c); setOpen(false); }}
                                style={{
                                    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                                    padding: "0.55rem 0.85rem", background: isSelected ? "#f0fdf4" : "transparent",
                                    border: "none", cursor: "pointer", fontSize: 13, fontWeight: isSelected ? 700 : 400,
                                    color: isSelected ? "#166534" : "var(--text)", textAlign: "left",
                                }}
                                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f8fafc"; }}
                                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                            >
                                <div>
                                    <div style={{ fontWeight: isSelected ? 700 : 500 }}>{c.name}</div>
                                    <div style={{ fontSize: 11, color: "#64748b" }}>{c.code} · {c.discipline || "General"}</div>
                                </div>
                                {isSelected && <FiCheck size={13} color="#16a34a" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Year Selector Dropdown ───────────────────────────────────────────────────
export function YearSelector({ icon: Icon, iconColor, label, value, years = [], onSelect }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button
                type="button"
                onClick={() => setOpen((p) => !p)}
                style={{
                    display: "flex", alignItems: "center", gap: "0.4rem",
                    background: "#f8fafc", border: "1px solid var(--border)",
                    borderRadius: 8, padding: "5px 10px", cursor: "pointer",
                    fontSize: 12.5, color: "var(--text)", transition: "all 0.15s",
                    whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
            >
                {Icon && <Icon size={13} color={iconColor} />}
                <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {label}:
                </span>
                {value
                    ? <span style={{ fontWeight: 700, color: iconColor }}>{value}</span>
                    : <span style={{ color: "#ef4444", display: "flex", alignItems: "center", gap: 3 }}><FiAlertTriangle size={11} /> Not set</span>
                }
                <FiChevronDown size={11} color="#94a3b8" style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }} />
            </button>

            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 6px)", left: 0,
                    minWidth: 180, background: "#fff", border: "1px solid var(--border)",
                    borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    zIndex: 100, overflow: "hidden",
                }}>
                    <div style={{ padding: "0.4rem 0.75rem", fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid var(--border)" }}>
                        Select {label}
                    </div>
                    {years.length === 0 ? (
                        <div style={{ padding: "0.75rem", fontSize: 13, color: "var(--text-muted)" }}>No years found.</div>
                    ) : (
                        years.map((y) => {
                            const isSelected = y.label === value;
                            return (
                                <button key={y._id} type="button" onClick={() => { onSelect(y); setOpen(false); }}
                                    style={{
                                        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                                        padding: "0.55rem 0.85rem", background: isSelected ? `${iconColor}10` : "transparent",
                                        border: "none", cursor: "pointer", fontSize: 13, fontWeight: isSelected ? 700 : 400,
                                        color: isSelected ? iconColor : "var(--text)", textAlign: "left",
                                        transition: "background 0.1s",
                                    }}
                                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f8fafc"; }}
                                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                                >
                                    {y.label}
                                    {y.isActive && <span style={{ fontSize: 10, background: `${iconColor}20`, color: iconColor, padding: "1px 6px", borderRadius: 99, fontWeight: 700 }}>Active</span>}
                                    {isSelected && <FiCheck size={13} color={iconColor} />}
                                </button>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Header ───────────────────────────────────────────────────────────────────
export default function Header({ onMenuToggle }) {
    const {
        user, isLoggedIn, logout,
        academicYear, financialYear,
        academicYears, financialYears,
        selectAcademicYear, selectFinancialYear,
        collegeData, allColleges, selectedCollegeId, selectCollege,
    } = useApp();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        toast.success("Logged out successfully");
        navigate("/login", { replace: true });
    };

    return (
        <header className="main-header">
            {/* Hamburger — mobile only */}
            <button className="hamburger-btn" onClick={onMenuToggle} title="Toggle menu">
                <FiMenu size={20} />
            </button>

            {/* App title — visible on mobile when sidebar is hidden */}
            <span className="header-title">CICP — CMS</span>

            {/* Right section */}
            <div className="header-right">
                {/* College Selector (Super Admin only) */}
                {user?.role === "superadmin" && (
                    <CollegeSelector
                        label="Campus"
                        value={selectedCollegeId === "all" ? "All Campuses" : (collegeData?.name || "All Campuses")}
                        colleges={allColleges}
                        onSelect={selectCollege}
                    />
                )}

                {/* Academic Year */}
                <YearSelector
                    icon={FiCalendar}
                    iconColor="#003b67"
                    label="Academic Year"
                    value={academicYear?.label}
                    years={academicYears}
                    onSelect={selectAcademicYear}
                />

                {/* Financial Year */}
                <YearSelector
                    icon={FiDollarSign}
                    iconColor="#16a34a"
                    label="Financial Year"
                    value={financialYear?.label}
                    years={financialYears}
                    onSelect={selectFinancialYear}
                />

                {/* Divider */}
                <div style={{ width: 1, height: 24, background: "var(--border)", flexShrink: 0 }} />

                {/* User */}
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: 13, color: "var(--text)" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#1e3a5f", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <FiUser size={13} color="#93c5fd" />
                    </div>
                    <span style={{ fontWeight: 600, whiteSpace: "nowrap" }} className="header-username">
                        {isLoggedIn ? (user?.displayName || user?.username || "User") : "Public"}
                    </span>
                </div>

                {/* Logout */}
                <button
                    onClick={handleLogout}
                    style={{
                        display: "flex", alignItems: "center", gap: "0.35rem",
                        background: "transparent", border: "1px solid var(--border)",
                        color: "var(--text-muted)", borderRadius: 8,
                        padding: "5px 10px", fontSize: 12.5, cursor: "pointer",
                        transition: "all 0.15s", whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#dc2626"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#dc2626"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border)"; }}
                >
                    <FiLogOut size={13} />
                    <span className="logout-label">Logout</span>
                </button>
            </div>
        </header>
    );
}